import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FeatureName,
  Group,
  RoleKey,
  RoleNameMap,
  Severity,
  useCurrentAccount,
  useGroups,
  useIsFeatureEnabled,
  useWorkspaceLabels,
} from '@gripp/shared-logic';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { tokens } from '../crop-circle/tokens';
import { Button } from '../crop-circle/Button';
import { Toggle } from '../crop-circle/Components/Toggle';
import { NotificationPreferencesOld } from './notificationPreferences-old';
import { preferencesStyles } from './styles';
import { notificationsSection } from './utils';

type NotificationPreferencesProps = {
  control: Control<any>;
  disabled?: boolean;
};

export const NotificationPreferences = ({
  control,
  disabled,
}: NotificationPreferencesProps) => {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleRedesignEnabled) {
    return (
      <NotificationPreferencesBase control={control} disabled={disabled} />
    );
  }

  return <NotificationPreferencesOld control={control} disabled={disabled} />;
};

const NotificationPreferencesBase = ({
  control,
  disabled,
}: NotificationPreferencesProps) => {
  const { t } = useTranslation();
  const { account } = useCurrentAccount();
  const { groups } = useGroups(account?.workspace?.id);
  const { labels } = useWorkspaceLabels(account?.workspace?.id);

  const canSelectLabelNotifications = Boolean(
    account?.roles?.some(
      (role) =>
        role.name === RoleNameMap[RoleKey.GENERAL] ||
        role.name === RoleNameMap[RoleKey.WORKSPACE_ADMIN] ||
        role.name === RoleNameMap[RoleKey.GLOBAL_ADMIN]
    )
  );

  return (
    <View style={preferencesStyles.contentContainer}>
      {/* GENERAL Section */}
      <View>
        <Text style={preferencesStyles.heading}>GENERAL</Text>
        <View style={styles.buttonContainer}>
          <GeneralNotificationButtons control={control} disabled={disabled} />
        </View>
      </View>

      {/* Activity Label Notifications Section */}
      {account &&
        account.workspace &&
        canSelectLabelNotifications &&
        labels &&
        labels.length > 0 && (
          <View>
            <Text style={preferencesStyles.heading}>ACTIVITY LABELS</Text>
            <View style={styles.buttonContainer}>
              <LabelNotificationButtons
                control={control}
                labels={labels}
                disabled={disabled}
              />
            </View>
          </View>
        )}

      {/* GROUPS Section */}
      {account && account.workspace && groups && groups.length > 0 && (
        <View>
          <Text style={preferencesStyles.heading}>GROUPS</Text>
          <View style={styles.buttonContainer}>
            <GroupNotificationButtons
              control={control}
              groups={groups}
              disabled={disabled}
            />
          </View>
        </View>
      )}
    </View>
  );
};

// General Notification Buttons Component
type GeneralNotificationButtonsProps = {
  control: Control<any>;
  disabled?: boolean;
};

const GeneralNotificationButtons = ({
  control,
  disabled,
}: GeneralNotificationButtonsProps) => {
  const { t } = useTranslation();
  const allSeverities = [Severity.Red, Severity.Purple, Severity.Blue];

  return (
    <Controller
      name="notificationPreferences"
      control={control}
      render={({ field: { onChange, value } }) => {
        const newIssueSeverities = value?.newIssueSeverities || [];
        const issueReplies = value?.issueReplies || false;
        // Default-on: treat null/undefined as opted-in (matches backend behaviour)
        const campaignMessages = value?.campaignMessages ?? true;

        const allSelected =
          allSeverities.every((s) => newIssueSeverities?.includes(s)) &&
          issueReplies;

        const handleAllToggle = () => {
          if (allSelected) {
            // Deselect all
            onChange({
              ...value,
              newIssueSeverities: [],
              issueReplies: false,
            });
          } else {
            // Select all
            onChange({
              ...value,
              newIssueSeverities: allSeverities,
              issueReplies: true,
            });
          }
        };

        const handleSeverityToggle = (severity: Severity) => {
          const current = newIssueSeverities;
          const newSeverities = current.includes(severity)
            ? current.filter((s: string) => s !== severity)
            : [...current, severity];
          onChange({
            ...value,
            newIssueSeverities: newSeverities,
          });
        };

        const handleRepliesToggle = () => {
          onChange({
            ...value,
            issueReplies: !issueReplies,
          });
        };

        const handleCampaignMessagesToggle = (next: boolean) => {
          onChange({
            ...value,
            campaignMessages: next,
          });
        };

        return (
          <>
            {/* All Button */}
            <Button
              variant="rounded"
              state={allSelected ? 'active' : 'default'}
              onPress={handleAllToggle}
              disabled={disabled}
              style={styles.button}
            >
              All
            </Button>

            {/* Individual Severity Buttons */}
            {notificationsSection
              .filter((n) => n.name === 'newIssueSeverities')
              .map((notification) => {
                const isSelected = newIssueSeverities?.includes(
                  notification.value
                );
                return (
                  <Button
                    key={notification.value}
                    variant="rounded"
                    state={isSelected ? 'active' : 'default'}
                    onPress={() =>
                      handleSeverityToggle(notification.value as Severity)
                    }
                    disabled={disabled}
                    style={styles.button}
                    leftIcon={
                      <View
                        style={[
                          styles.iconContainer,
                          { backgroundColor: notification.backgroundColor },
                        ]}
                      >
                        <MaterialCommunityIcons
                          name={notification.icon as any}
                          size={notification.sizeIcon}
                          color={notification.color}
                        />
                      </View>
                    }
                  >
                    {t(notification.labelKey)}
                  </Button>
                );
              })}

            {/* Messages/Replies Button */}
            {notificationsSection
              .filter((n) => n.name === 'issueReplies')
              .map((notification) => {
                return (
                  <Button
                    key={notification.value}
                    variant="rounded"
                    state={issueReplies ? 'active' : 'default'}
                    onPress={handleRepliesToggle}
                    disabled={disabled}
                    style={styles.button}
                    leftIcon={
                      <MaterialCommunityIcons
                        name={notification.icon as any}
                        size={notification.sizeIcon}
                        color={issueReplies ? '#FFFFFF' : '#020202'}
                      />
                    }
                  >
                    {t(notification.labelKey)}
                  </Button>
                );
              })}

            {/* Campaign Messages Toggle */}
            <View style={styles.campaignToggleRow}>
              <Toggle
                value={campaignMessages}
                onValueChange={handleCampaignMessagesToggle}
                label={t('preferences.notifications.campaignMessages')}
                disabled={disabled}
                testID="campaign-messages-toggle"
              />
            </View>
          </>
        );
      }}
    />
  );
};

// Label Notification Buttons Component
type LabelNotificationButtonsProps = {
  control: Control<any>;
  labels: Array<{ id: string; name: string }>;
  disabled?: boolean;
};

const LabelNotificationButtons = ({
  control,
  labels,
  disabled,
}: LabelNotificationButtonsProps) => {
  const { t } = useTranslation();

  return (
    <Controller
      name="notificationPreferences.labelIds"
      control={control}
      render={({ field: { onChange, value = [] } }) => {
        const allSelected =
          labels.length > 0 && labels.every((l) => value?.includes(l.id));

        const handleAllToggle = () => {
          if (allSelected) {
            onChange([]);
          } else {
            onChange(labels.map((l) => l.id));
          }
        };

        const handleLabelToggle = (labelId: string) => {
          const newValue = value?.includes(labelId)
            ? value.filter((id: string) => id !== labelId)
            : [...(value || []), labelId];
          onChange(newValue);
        };

        return (
          <>
            {/* All Button */}
            <Button
              variant="rounded"
              state={allSelected ? 'active' : 'default'}
              onPress={handleAllToggle}
              disabled={disabled}
              style={styles.button}
            >
              All
            </Button>

            {/* Individual Label Buttons */}
            {labels.map((label) => {
              const isSelected = value?.includes(label.id);
              return (
                <Button
                  key={label.id}
                  variant="rounded"
                  state={isSelected ? 'active' : 'default'}
                  onPress={() => handleLabelToggle(label.id)}
                  disabled={disabled}
                  style={styles.button}
                >
                  {label.name}
                </Button>
              );
            })}
          </>
        );
      }}
    />
  );
};

// Group Notification Buttons Component
type GroupNotificationButtonsProps = {
  control: Control<any>;
  groups: Group[];
  disabled?: boolean;
};

const GroupNotificationButtons = ({
  control,
  groups,
  disabled,
}: GroupNotificationButtonsProps) => {
  const { t } = useTranslation();

  return (
    <Controller
      name="notificationPreferences.groupIds"
      control={control}
      render={({ field: { onChange, value = [] } }) => {
        const allSelected =
          groups.length > 0 && groups.every((g) => value?.includes(g.id));

        const handleAllToggle = () => {
          if (allSelected) {
            onChange([]);
          } else {
            onChange(groups.map((g) => g.id));
          }
        };

        const handleGroupToggle = (groupId: string) => {
          const newValue = value?.includes(groupId)
            ? value.filter((id: string) => id !== groupId)
            : [...(value || []), groupId];
          onChange(newValue);
        };

        return (
          <>
            {/* All Button */}
            <Button
              variant="rounded"
              state={allSelected ? 'active' : 'default'}
              onPress={handleAllToggle}
              disabled={disabled}
              style={styles.button}
            >
              All
            </Button>

            {/* Individual Group Buttons */}
            {groups.map((group) => {
              const isSelected = value?.includes(group.id);
              return (
                <Button
                  key={group.id}
                  variant="rounded"
                  state={isSelected ? 'active' : 'default'}
                  onPress={() => handleGroupToggle(group.id)}
                  disabled={disabled}
                  style={styles.button}
                >
                  {group.name}
                </Button>
              );
            })}
          </>
        );
      }}
    />
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
    backgroundColor: tokens.colors.brand.white,
    borderRadius: tokens.borderRadius.xl,
    paddingVertical: tokens.spacing.lg,
  },
  button: {
    marginBottom: tokens.spacing.sm,
  },
  iconContainer: {
    width: 20,
    height: 20,
    borderRadius: tokens.borderRadius.sm,
    justifyContent: 'center',
    alignItems: 'center',
  },
  campaignToggleRow: {
    width: '100%',
    paddingVertical: tokens.spacing.xs,
  },
});
